import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ScheduleDeletionPreviewEvent } from '../../../../../../models/event-type';
import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../utils';

@Component({
  selector: 'app-schedule-orphaned-events-modal',
  templateUrl: './schedule-orphaned-events-modal.component.html',
  styleUrls: ['./schedule-orphaned-events-modal.component.css']
})
export class ScheduleOrphanedEventsModalComponent {
  /**
   * A modal warning the user that deleting a schedule will leave the listed
   * events orphaned. The user may proceed with the deletion (orphaning the
   * events) or cancel.
   *
   * Why an event is left behind depends on the schedule type, so the reason is
   * passed in rather than assumed: on most types it is that the event has
   * active reservations and cannot be removed automatically, while on a rolling
   * type — where everything else is deleted outright — it is that the event
   * granted a membership benefit.
   */

  // The name of the schedule (or schedule exception) being deleted, used in the
  // warning message. May be empty (names are optional), in which case the
  // message refers to "this schedule" generically.
  @Input() scheduleName = '';

  // The noun for the thing being deleted, e.g. 'schedule' or 'schedule
  // exception'. Used in the modal title and warning message.
  @Input() subjectNoun = 'schedule';

  // Why the listed events cannot be removed, completing the sentence
  // "...will leave the following event(s) in place, because ...". Both forms
  // are supplied so the surrounding copy stays grammatical for one event or
  // several.
  @Input() remainingReasonSingular =
    'it has active reservations and cannot be removed automatically';
  @Input() remainingReasonPlural =
    'they have active reservations and cannot be removed automatically';

  // An additional warning about what the deletion WILL destroy, shown above the
  // list. Empty when the deletion removes nothing (or nothing booked).
  @Input() bookingWarning = '';

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;

  // The events which would be left orphaned by the deletion.
  @Input() remainingEvents: ScheduleDeletionPreviewEvent[] = [];

  // Called when the user confirms the deletion.
  @Input() confirmedCallback: () => void = null;

  // Set once the user confirms, to guard against a double-submit and to drive
  // the button's loading state (mirrors SimpleMessageModalComponent).
  public confirming = false;

  constructor(
    public activeModal: NgbActiveModal
  ) {}

  public handleConfirm() {
    if (this.confirming) {
      return;
    }
    this.confirming = true;

    // Dismiss in a finally so a callback that throws synchronously can't leave
    // the modal open with a permanently disabled Confirm button.
    try {
      if (this.confirmedCallback !== null) {
        this.confirmedCallback();
      }
    } finally {
      this.activeModal.dismiss();
    }
  }

  public getEventNameDisplay(event: ScheduleDeletionPreviewEvent): string {
    if (event.occurrence_name) {
      return event.event_type_name + ' — ' + event.occurrence_name;
    }
    return event.event_type_name;
  }

  // The phrase used to refer to the subject in the warning message, naming it
  // when a name is available and falling back to a generic reference otherwise.
  public getSubjectDescription(): string {
    if (this.scheduleName) {
      return 'the ' + this.subjectNoun + ' "' + this.scheduleName + '"';
    }
    return 'this ' + this.subjectNoun;
  }
}
